export function parseLocalDateValue(value) {
  if (!value) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split("-").map(Number);
    return new Date(year, month - 1, day);
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function startOfLocalDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function startOfCurrentWeek(now = new Date()) {
  const start = startOfLocalDay(now);
  const day = start.getDay();
  const delta = day === 0 ? -6 : 1 - day;
  start.setDate(start.getDate() + delta);
  return start;
}

export function getLocalDateKey(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function toLocalDateKey(value) {
  const date = parseLocalDateValue(value);
  return date ? getLocalDateKey(date) : null;
}

export function dayDiffFromNow(value, now = new Date()) {
  const date = parseLocalDateValue(value);
  if (!date) return null;
  return Math.round(
    (startOfLocalDay(now).getTime() - startOfLocalDay(date).getTime()) / 86400000
  );
}

export function uniqueDateValues(values) {
  return Array.from(new Set((values || []).filter(Boolean)));
}

export function uniqueLocalDateKeys(values) {
  return uniqueDateValues(values)
    .map((value) => toLocalDateKey(value))
    .filter(Boolean);
}

export function countByLocalDate(values) {
  return (values || []).reduce((acc, value) => {
    const key = toLocalDateKey(value);
    if (!key) return acc;
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
}

export function countUniqueDaysWithin(values, days, now = new Date()) {
  return uniqueDateValues(values).filter((value) => {
    const diff = dayDiffFromNow(value, now);
    return diff !== null && diff >= 0 && diff < days;
  }).length;
}

export function countEventsWithin(values, days, now = new Date()) {
  return (values || []).filter((value) => {
    const diff = dayDiffFromNow(value, now);
    return diff !== null && diff >= 0 && diff < days;
  }).length;
}

export function countUniqueDaysInCurrentWeek(values, now = new Date()) {
  const weekStart = startOfCurrentWeek(now).getTime();
  const weekEnd = startOfLocalDay(now).getTime();
  return uniqueDateValues(values).filter((value) => {
    const date = parseLocalDateValue(value);
    if (!date) return false;
    const time = startOfLocalDay(date).getTime();
    return time >= weekStart && time <= weekEnd;
  }).length;
}

export function countEventsInCurrentWeek(values, now = new Date()) {
  const weekStart = startOfCurrentWeek(now).getTime();
  const weekEnd = startOfLocalDay(now).getTime();
  return (values || []).filter((value) => {
    const date = parseLocalDateValue(value);
    if (!date) return false;
    const time = startOfLocalDay(date).getTime();
    return time >= weekStart && time <= weekEnd;
  }).length;
}

export function getCurrentWeekElapsedDays(now = new Date()) {
  return Math.min(now.getDay() === 0 ? 7 : now.getDay(), 7);
}

export function collectCompletionDates(commitments, completionOverrideMap = {}, now = new Date()) {
  const todayKey = getLocalDateKey(now);
  return (commitments || []).flatMap((commitment) => {
    const completionDates = (commitment?.completions || [])
      .filter((completion) => completion?.completed && completion?.date)
      .map((completion) => completion.date);
    const completedToday = Boolean(
      completionOverrideMap[commitment?.id] ?? commitment?.completed_today
    );
    const hasTodayCompletion = completionDates.some((value) => toLocalDateKey(value) === todayKey);
    if (completedToday && !hasTodayCompletion) {
      return [...completionDates, todayKey];
    }
    return completionDates;
  });
}

export function describeVisibleMovement({ checkins = 0, actions = 0 }) {
  if (checkins > 0 && actions > 0) {
    return `${checkins === 1 ? "Check-in" : `${checkins} check-ins`} and ${actions} completed ${actions === 1 ? "habit" : "habits"}`;
  }
  if (actions > 0) {
    return `${actions} completed ${actions === 1 ? "habit" : "habits"}`;
  }
  if (checkins > 0) {
    return "Check-in only";
  }
  return "No visible activity";
}

export function buildSevenDayMovementChart({
  checkinDates = [],
  actionCompletionDates = [],
  now = new Date(),
}) {
  const checkinKeys = new Set(uniqueLocalDateKeys(checkinDates));
  const actionCounts = countByLocalDate(actionCompletionDates);
  const items = [];
  for (let offset = 6; offset >= 0; offset -= 1) {
    const date = new Date(now);
    date.setDate(now.getDate() - offset);
    const key = getLocalDateKey(date);
    const checkins = checkinKeys.has(key) ? 1 : 0;
    const actionCount = actionCounts[key] || 0;
    const level = Math.min(checkins + Math.min(actionCount, 2), 3);
    items.push({
      key,
      label: date.toLocaleDateString(undefined, { weekday: "short" }).slice(0, 3),
      fullLabel: date.toLocaleDateString(undefined, {
        weekday: "long",
        month: "short",
        day: "numeric",
      }),
      active: level > 0,
      level,
      checkins,
      actionCount,
      summary: describeVisibleMovement({ checkins, actions: actionCount }),
    });
  }
  return items;
}

export function buildCurrentWeekMovementChart({
  checkinDates = [],
  actionCompletionDates = [],
  now = new Date(),
}) {
  const checkinKeys = new Set(uniqueLocalDateKeys(checkinDates));
  const actionCounts = countByLocalDate(actionCompletionDates);
  const start = startOfCurrentWeek(now);
  const items = [];
  for (let offset = 0; offset < 7; offset += 1) {
    const date = new Date(start);
    date.setDate(start.getDate() + offset);
    const key = getLocalDateKey(date);
    const checkins = checkinKeys.has(key) ? 1 : 0;
    const actionCount = actionCounts[key] || 0;
    const level = Math.min(checkins + Math.min(actionCount, 2), 3);
    items.push({
      key,
      label: date.toLocaleDateString(undefined, { weekday: "short" }).slice(0, 3),
      fullLabel: date.toLocaleDateString(undefined, {
        weekday: "long",
        month: "short",
        day: "numeric",
      }),
      active: level > 0,
      level,
      checkins,
      actionCount,
      summary: describeVisibleMovement({ checkins, actions: actionCount }),
      today: offset === getCurrentWeekElapsedDays(now) - 1,
    });
  }
  return items;
}

export function normalizeCanonicalSummary(summary, fallback = {}) {
  const source = summary || fallback || {};
  return {
    actedToday: Boolean(
      source.acted_today ??
        source.hasShownUpToday ??
        source.has_shown_up_today ??
        source.minimum_daily_behavior_met
    ),
    activeDaysLast7: Number(
      source.active_days_last_7 ??
        source.activeDaysLast7 ??
        source.recent_active_days ??
        0
    ),
    usageStreak: Number(
      source.usage_streak ??
        source.usageStreak ??
        source.current_usage_streak ??
        0
    ),
    actionsOpenToday: Number(
      source.actions_open_today ??
        source.actionsOpenToday ??
        source.due_actions_count ??
        0
    ),
    actionsCompletedToday: Number(
      source.actions_completed_today ??
        source.actionsCompletedToday ??
        0
    ),
    actionsCompletedThisWeek: Number(
      source.actions_completed_this_week ??
        source.actionsCompletedThisWeek ??
        0
    ),
    sprintActive: Boolean(
      source.sprint_active ??
        source.sprintActive ??
        source.active_sprint_id
    ),
    userState:
      source.user_state ||
      source.userState ||
      source.state ||
      "drift",
  };
}
